import { useCallback, useEffect, useState } from 'react';
import { Service } from '@/types/utils';

const RECENT_SERVICES_KEY = 'recentServices';
const MAX_RECENT_SERVICES = 10;
const MAX_DISPLAY_SERVICES = 3;

interface RecentService {
  service: Service;
  lastAccessedAt: string;
}

export function useRecentServices() {
  const [recentServices, setRecentServices] = useState<RecentService[]>([]);

  // Load recent services from localStorage on mount
  useEffect(() => {
    const loadRecentServices = () => {
      try {
        const stored = localStorage.getItem(RECENT_SERVICES_KEY);
        if (stored) {
          const parsed = JSON.parse(stored) as RecentService[];
          setRecentServices(parsed);
        }
      } catch (error) {
        console.error('Failed to load recent services:', error);
      }
    };

    loadRecentServices();
  }, []);

  // Track a service visit
  const trackServiceVisit = useCallback((service: Service) => {
    if (!service || !service.service_uuid) return;

    try {
      const stored = localStorage.getItem(RECENT_SERVICES_KEY);
      let recent: RecentService[] = stored ? JSON.parse(stored) : [];

      // Remove the service if it already exists in the list
      recent = recent.filter(item => item.service.service_uuid !== service.service_uuid);

      // Add the service to the beginning of the list
      const newEntry: RecentService = {
        service,
        lastAccessedAt: new Date().toISOString()
      };
      recent.unshift(newEntry);

      // Keep only the most recent services
      recent = recent.slice(0, MAX_RECENT_SERVICES);

      // Save to localStorage
      localStorage.setItem(RECENT_SERVICES_KEY, JSON.stringify(recent));
      setRecentServices(recent);
    } catch (error) {
      console.error('Failed to track service visit:', error);
    }
  }, []);

  // Get the most recent services for display
  const getDisplayServices = useCallback(() => {
    return recentServices.slice(0, MAX_DISPLAY_SERVICES);
  }, [recentServices]);

  // Clear recent services
  const clearRecentServices = useCallback(() => {
    try {
      localStorage.removeItem(RECENT_SERVICES_KEY);
      setRecentServices([]);
    } catch (error) {
      console.error('Failed to clear recent services:', error);
    }
  }, []);

  return {
    recentServices,
    displayServices: getDisplayServices(),
    trackServiceVisit,
    clearRecentServices
  };
}